import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Globe, Mic, Download, LogOut, RefreshCw, Loader2, Inbox } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { LOGO, API } from "../lib/site";

const TABS = [
  { id: "website-preview", label: "Website Previews", icon: Globe, accent: "cyan" },
  { id: "voice-agent", label: "Voice Agent Inquiries", icon: Mic, accent: "violet" },
];

const COLUMNS = {
  "website-preview": [
    ["created_at", "Date"],
    ["business_name", "Business"],
    ["name", "Contact"],
    ["email", "Email"],
    ["phone", "Phone"],
    ["business_type", "Type"],
    ["google_business_url", "Google Profile"],
  ],
  "voice-agent": [
    ["created_at", "Date"],
    ["business_name", "Business"],
    ["name", "Contact"],
    ["email", "Email"],
    ["phone", "Phone"],
    ["business_type", "Industry"],
    ["call_volume", "Volume"],
    ["voice_functions", "Functions"],
    ["preferred_call_time", "Callback"],
  ],
};

const fmtDate = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return v;
  }
};

const cellValue = (lead, key) => {
  const v = lead[key];
  if (key === "created_at") return fmtDate(v);
  if (Array.isArray(v)) return v.join(", ");
  return v || "—";
};

export default function AdminDashboard() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("website-preview");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const handleAuthFail = useCallback(() => {
    setUser(false);
    navigate("/admin/login");
  }, [setUser, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads/${tab}`, { withCredentials: true });
      setLeads(data.leads);
    } catch (err) {
      if (err.response?.status === 401) return handleAuthFail();
      toast.error("Could not load leads.");
    } finally {
      setLoading(false);
    }
  }, [tab, handleAuthFail]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads/${tab}/export`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `talklogical-${tab}-leads.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch (err) {
      if (err.response?.status === 401) return handleAuthFail();
      toast.error("Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const activeTab = TABS.find((t) => t.id === tab);

  return (
    <div className="min-h-screen bg-[#07090E] text-slate-100 font-body" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-[#07090E]/85 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/95 ring-1 ring-cyan-400/40">
              <img src={LOGO} alt="TalkLogical logo" className="h-7 w-7 object-contain" />
            </span>
            <div>
              <p className="font-display font-bold text-sm tracking-tight text-slate-50">Leads Dashboard</p>
              <p className="text-[10px] font-mono text-slate-500">{user?.email}</p>
            </div>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={async () => { await logout(); navigate("/admin/login"); }}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-slate-300 hover:border-red-400/50 hover:text-red-300 transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex gap-3" data-testid="admin-tabs">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  data-testid={`admin-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2.5 rounded-full border px-5 py-2.5 text-sm font-semibold transition-[border-color,background-color,color] duration-300 ${
                    tab === t.id
                      ? t.accent === "cyan"
                        ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-200"
                        : "border-violet-400/60 bg-violet-500/15 text-violet-200"
                      : "border-white/10 bg-white/5 text-slate-400 hover:border-white/25 hover:text-slate-200"
                  }`}
                >
                  <t.icon className="h-4 w-4" />
                  {t.label}
                </button>
              ))}
            </div>
            <div className="flex gap-3">
              <button
                data-testid="admin-refresh-button"
                onClick={load}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-xs font-semibold text-slate-300 hover:border-cyan-400/50 hover:text-cyan-200 transition-colors"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Refresh
              </button>
              <button
                data-testid="admin-export-button"
                onClick={exportCsv}
                disabled={exporting || leads.length === 0}
                className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2.5 text-xs font-bold text-[#07090E] hover:bg-cyan-300 disabled:opacity-50 transition-colors"
              >
                {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                Export CSV
              </button>
            </div>
          </div>

          <div className="mt-8 glow-card rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <p className="text-sm font-semibold text-slate-200" data-testid="admin-table-title">
                {activeTab.label}
              </p>
              <span className="rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs font-mono text-slate-400" data-testid="admin-lead-count">
                {leads.length} lead{leads.length === 1 ? "" : "s"}
              </span>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-24" data-testid="admin-table-loading">
                <Loader2 className="h-7 w-7 text-cyan-400 animate-spin" />
              </div>
            ) : leads.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="admin-table-empty">
                <Inbox className="h-10 w-10 text-slate-600 mb-4" />
                <p className="text-sm text-slate-400">No {activeTab.label.toLowerCase()} yet.</p>
                <p className="mt-1 text-xs text-slate-600">New submissions will appear here instantly.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm" data-testid="admin-leads-table">
                  <thead>
                    <tr className="border-b border-white/10">
                      {COLUMNS[tab].map(([key, label]) => (
                        <th key={key} className="px-6 py-3.5 text-[10px] font-mono font-semibold tracking-widest uppercase text-slate-500 whitespace-nowrap">
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => (
                      <tr key={lead.id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors" data-testid={`admin-lead-row-${lead.id.slice(0, 8)}`}>
                        {COLUMNS[tab].map(([key]) => (
                          <td key={key} className="px-6 py-4 text-xs text-slate-300 max-w-[220px] truncate whitespace-nowrap">
                            {cellValue(lead, key)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
